import { FC, useCallback, useMemo, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { ActiveFilters, SavedView } from "@app/network-portfolio-management/interfaces";
import { filterSummary, toSavedView } from "@app/network-portfolio-management/utils/savedViewsUtils";
import { useFilterContext } from "@app/network-portfolio-management/contexts/FilterContext";
import NetworkPortfolioManagementService from "@app/network-portfolio-management/services/NetworkPortfolioManagementService";
import { Modal } from "@app/shared/Modals";
import { Toggle } from "@app/shared/Toggle";
import { QESTextInput } from "@app/shared/qes-standards/forms/TextInput";
import { QESTextArea } from "@app/shared/qes-standards/forms/TextArea";
import { QESButton } from "@app/shared/qes-standards/Button";
import { truncateStringWithEllipsis } from "@app/shared/utility/truncateStringWithEllipsis";

const MAX_LENGTH = 60;
// truncateStringWithEllipsis slices to the given length then appends a single
// ellipsis char, so MAX_LENGTH - 1 + the ellipsis = MAX_LENGTH, matching the name input's maxLength.
const MAX_VIEW_NAME_LENGTH = MAX_LENGTH - 1;

function getNameSuggestion(filters: ActiveFilters): string {
  return truncateStringWithEllipsis(filterSummary(filters), MAX_VIEW_NAME_LENGTH) || "My View";
}

interface SaveAsViewModalProps {
  open: boolean;
  onClose: () => void;
  onSaved: (view: SavedView, replacedId?: number) => void;
  /** When provided, the modal opens in edit mode pre-populated with this view's data. */
  editingView?: SavedView;
}

export const SaveAsViewModal: FC<SaveAsViewModalProps> = ({ open, onClose, onSaved, editingView }) => {
  const npmService = useRef(new NetworkPortfolioManagementService()).current;
  const { activeFilters, savedViews } = useFilterContext();

  const filtersToSave = editingView?.filters ?? activeFilters;
  const isEditMode = editingView !== undefined;

  const [name, setName] = useState(() => editingView?.name ?? getNameSuggestion(activeFilters));
  const [description, setDescription] = useState(() => editingView?.description ?? "");
  const [isDefault, setIsDefault] = useState(() => editingView?.isDefault ?? false);
  const [nameError, setNameError] = useState<string | null>(null);
  const [duplicateConflict, setDuplicateConflict] = useState(false);
  const [conflictingViewId, setConflictingViewId] = useState<number | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Name of the current default view (excluding the view being edited), for the replacement warning.
  const currentDefaultName = useMemo(() => {
    const current = savedViews.find(
      (v) => !v.isQesView && v.isDefault && v.npmFilteredViewId !== editingView?.npmFilteredViewId,
    );
    return current?.name ?? null;
  }, [editingView?.npmFilteredViewId, savedViews]);

  const handleClose = useCallback(() => {
    setName(editingView?.name ?? getNameSuggestion(activeFilters));
    setDescription(editingView?.description ?? "");
    setIsDefault(editingView?.isDefault ?? false);
    setNameError(null);
    setDuplicateConflict(false);
    setConflictingViewId(null);
    setSaveError(null);
    onClose();
  }, [activeFilters, editingView, onClose]);

  const updateMutation = useMutation({
    mutationFn: (viewName: string) =>
      npmService.updateSavedView(editingView!.npmFilteredViewId, {
        description,
        filters: filtersToSave,
        isDefault,
        name: viewName,
      }),
    onError: () => setSaveError("Failed to update view. Please try again."),
    onSuccess: (response) => {
      onSaved(toSavedView(response), editingView!.npmFilteredViewId);
      handleClose();
    },
  });

  const createMutation = useMutation({
    mutationFn: (args: { viewName: string; replacedId?: number }) =>
      npmService.createSavedView({
        description,
        filters: filtersToSave,
        isDefault,
        name: args.viewName,
      }),
    onError: (e: unknown, { viewName }) => {
      const status = e !== null && typeof e === "object" && "status" in e
        ? (e as { status: unknown }).status : undefined;
      if (status === 409) {
        const existing = savedViews.find((v) => !v.isQesView && v.name.toLowerCase() === viewName.toLowerCase());
        setConflictingViewId(existing?.npmFilteredViewId ?? null);
        setDuplicateConflict(true);
      } else {
        setSaveError("Failed to save view. Please try again.");
      }
    },
    onSuccess: (response, { replacedId }) => {
      onSaved(toSavedView(response), replacedId);
      handleClose();
    },
    
  });

  const overwriteMutation = useMutation({
    mutationFn: (args: { viewId: number; viewName: string }) =>
      npmService.updateSavedView(args.viewId, {
        description,
        filters: filtersToSave,
        isDefault,
        name: args.viewName,
      }),
    onError: () => setSaveError("Failed to overwrite view. Please try again."),
    onSuccess: (response, { viewId }) => {
      setDuplicateConflict(false);
      onSaved(toSavedView(response), viewId);
      handleClose();
    },
  });

  const submitting = updateMutation.isPending || createMutation.isPending || overwriteMutation.isPending;

  const handleSave = useCallback(async () => {
    setSaveError(null);
    const trimmed = name.trim();
    if (!trimmed) {
      setNameError("Name is required.");
      return;
    }
    // Names matching a QES system view (e.g. the synthetic "All Projects") are
    // reserved. The synthetic doesn't exist server-side, so the server won't
    // 409 on it — we have to catch the collision client-side. Applies to both
    // create and edit modes.
    const reservedConflict = savedViews.some(
      (v) => v.isQesView && v.name.trim().toLowerCase() === trimmed.toLowerCase(),
    );
    if (reservedConflict) {
      setNameError(`A view named "${trimmed}" already exists.`);
      return;
    }
    // In edit mode, also reject collisions with other user-created views.
    // Editing a view is not a path to overwrite a different one; the user
    // must pick a unique name. (Create mode lets the user pick rename or
    // overwrite via the conflict UI when the server returns 409.)
    if (isEditMode && editingView) {
      const userCollision = savedViews.some(
        (v) => v.npmFilteredViewId !== editingView.npmFilteredViewId &&
               !v.isQesView &&
               v.name.trim().toLowerCase() === trimmed.toLowerCase(),
      );
      if (userCollision) {
        setNameError(`A view named "${trimmed}" already exists.`);
        return;
      }
    }
    setNameError(null);
    if (isEditMode) {
      updateMutation.mutate(trimmed);
    } else {
      createMutation.mutate({ viewName: trimmed });
    }
  }, [createMutation, editingView, isEditMode, name, savedViews, updateMutation]);

  const handleRename = useCallback(() => {
    setDuplicateConflict(false);
    setConflictingViewId(null);
    setName("");
  }, []);

  // handleOverwrite is only reachable in create mode — edit-mode duplicates
  // are blocked at the validation step in handleSave.
  const handleOverwrite = useCallback(() => {
    if (conflictingViewId === null) {
      setSaveError("Cannot overwrite: the conflicting view could not be identified. Please rename.");
      return;
    }
    overwriteMutation.mutate({ viewId: conflictingViewId, viewName: name.trim() });
  }, [conflictingViewId, name, overwriteMutation]);

  const modalBody = useMemo(() => (
    <div className="qes-npm-save-view-modal">
      {saveError && (
        <div className="alert alert-danger mb-3" role="alert" data-testid="npm-save-view-error">
          {saveError}
        </div>
      )}
      {duplicateConflict ? (
        <div className="qes-npm-save-view-modal__conflict">
          <p>A view named <strong>{name.trim()}</strong> already exists.</p>
          <p>Would you like to rename this view or overwrite the existing one?</p>
          <div className="qes-npm-save-view-modal__conflict-actions">
            <QESButton
              qesType="secondary"
              qesTestId="npm-save-view-rename"
              onClick={handleRename}
              disabled={submitting}
            >
              Rename
            </QESButton>
            <QESButton
              qesType="primary-danger"
              qesTestId="npm-save-view-overwrite"
              onClick={handleOverwrite}
              disabled={submitting}
            >
              Overwrite
            </QESButton>
          </div>
        </div>
      ) : (
        <>
          <QESTextInput
            label="View Name"
            testId="npm-save-view-name"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (nameError) setNameError(null);
            }}
            required
            isValid={!nameError && name.trim().length > 0}
            errorText={nameError ?? undefined}
            wasFocusedExternally={nameError !== null}
            containerClassName="mb-3"
            maxLength={MAX_LENGTH}
          />
          <QESTextArea
            label="Description (optional)"
            testId="npm-save-view-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            containerClassName="mb-3"
            maxLength={200}
            rows={5}
          />
          <div className="qes-npm-save-view-modal__default-row">
            <div className="qes-npm-save-view-modal__default-row-info">
              <div className="qes-npm-save-view-modal__default-row-label">Set as default view</div>
              <div className="qes-npm-save-view-modal__default-row-sub">Loads automatically when you open Portfolio Dashboard</div>
            </div>
            <Toggle
              active={isDefault}
              onChange={(current) => setIsDefault(!current)}
              testId="npm-save-view-set-default"
            />
          </div>
          {isDefault && currentDefaultName && (
            <div className="qes-npm-save-view-modal__default-warning" data-testid="npm-save-view-default-warning">
              This will replace &ldquo;{currentDefaultName}&rdquo; as your default view.
            </div>
          )}
        </>
      )}
    </div>
  ), [currentDefaultName, description, duplicateConflict, handleOverwrite, handleRename, isDefault, name, nameError, saveError, submitting]);

  return (
    <Modal
      isVisible={open}
      closeModal={handleClose}
      hideBodyScroll
      content={{
        body: modalBody,
        cancelButtonText: "Cancel",
        confirmButtonAction: () => void handleSave(),
        confirmButtonDisable: submitting || duplicateConflict,
        confirmButtonText: "Save",
        confirmButtonType: "primary",
        title: isEditMode ? "Edit View" : "Save as View",
      }}
    />
  );
};
